"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Pure-CSS button — previously wrapped in a `motion.div` (framer-motion ~50 kB)
 * solely for a hover/tap micro-scale. The same effect is now done with a
 * GPU-accelerated CSS transform, which removes framer-motion from the initial
 * JS bundle of every page that uses a button.
 *
 * Behaviour is unchanged: the button still scales 1.02 on hover and 0.98 on
 * active, and `asChild` / `noScale` still suppress the effect.
 *
 * Respects `prefers-reduced-motion` via the `motion-safe:` Tailwind prefix.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 [&_svg]:mr-0 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring aria-invalid:border-destructive will-change-transform",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-white dark:text-black dark:hover:bg-neutral-100",
        destructive:
          "bg-destructive h-10 text-white hover:bg-destructive/90 dark:bg-destructive/60",
        outline:
          "border bg-white text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-10",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    noScale?: boolean;
  };

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  noScale = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  // Pure-CSS scale animation, only when the button is interactive and the user
  // hasn't opted out of motion. Identical visual feel to the previous spring.
  const scaleClasses =
    asChild || noScale
      ? ""
      : "cursor-pointer motion-safe:transition-transform motion-safe:duration-150 motion-safe:hover:scale-[1.02] motion-safe:active:scale-[0.98]";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size }), scaleClasses, className)}
      {...props}
    />
  );
}

export { Button, buttonVariants };
